import LinkIcon from '@atlaskit/icon/core/link';
import LinkBrokenIcon from '@atlaskit/icon/core/link-broken';
import { Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { messages as viewsMessages } from '@databases/api-views/messages';
import ValidationStatus, { Placement } from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { isEmbed } from '@databases/modes';
import { isMobile } from '@databases/modes';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { type DatabaseView } from '@databases/types/database/view';
import { isUnsavedView } from '@databases/utils/database/view/view';
import { showErrorSnackbar } from '@databases/utils/errors';
import { getLayoutIcons } from '@databases/view-settings/utils';
import { HoverButton } from '@databases/view-tabs/HoverButton';
import ViewActions from '@databases/view-tabs/ViewActions';
import { type ViewPanelSettingType } from '@databases/views/types';
import useCopyViewLink from '@databases/views/useCopyViewLink';
import React, {
  type FunctionComponent,
  type ReactChild,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import ViewCardNameEdit, { ViewNameContainer } from './ViewCardNameEdit';

const PADDING_VALUES = {
  DEFAULT_ACTIVE_PADDING: '70px',
  DEFAULT_PADDING: '6px',
  UNSAVED_VIEW_ACTIVE_PADDING: token('space.500', '40px'),
};

const DefaultText = styled.span`
  color: ${colors.subtleText};
  font: ${token('font.body')};
  font-style: italic;
  text-align: right;
  position: absolute;
  right: ${token('space.075', '6px')};
`;

const Container = styled.div<{
  isActive?: boolean;
  isDefault: boolean;
  isViewActionsOpen: boolean;
  isUnsavedView?: boolean;
  showViewSettings?: boolean;
  isReadonly?: boolean;
  $readonlyHoverPadding: string;
  $maxWidthOpenDefault: string;
  $maxWidthOpenNonDefault: string;
}>`
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: ${token('space.050', '4px')};

    z-index: 0;
    padding: ${token('space.100', '8px')} 0px;
    margin: 0px 0px 0px ${token('space.050', '4px')};

    border-radius: 3px;
    cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
    color: ${(props) =>
      props.isActive ? token('color.text.selected', colors.B300) : colors.N400};
}

& ${DefaultText} {
    right: ${(props) => {
      if (props.isViewActionsOpen) {
        return PADDING_VALUES.DEFAULT_ACTIVE_PADDING;
      }

      return PADDING_VALUES.DEFAULT_PADDING;
    }}
}

/* Only apply hover styles on devices that have hover capability (non-touch devices) */
@media (hover: hover) and (pointer: fine) {
    &:hover,
    &:focus-within {
        ${HoverButton} {
            width: 32px;
            visibility: visible;
            display: inline-flex;
            opacity: 1;
        }

        ${DefaultText} {
            ${(props) => {
              if (props.isUnsavedView) {
                return `right: ${PADDING_VALUES.UNSAVED_VIEW_ACTIVE_PADDING};`;
              }

              if (props.isReadonly) {
                return `right: ${props.$readonlyHoverPadding};`;
              }

              return `right: ${PADDING_VALUES.DEFAULT_ACTIVE_PADDING};`;
            }}
        }

        ${ViewNameContainer} {
            max-width: ${(props) => {
              return props.isDefault
                ? props.$maxWidthOpenDefault
                : props.$maxWidthOpenNonDefault;
            }} !important;
        }
    }
}

/* For touch devices, ensure hover buttons remain hidden to prevent double-tap issues */
@media (hover: none) or (pointer: coarse) {
    ${HoverButton} {
        width: 0;
        visibility: hidden;
        display: none;
        opacity: 0;
    }
}
`;

const SubtleText = styled.div`
  color: ${colors.subtleText};
  font: ${token('font.body.UNSAFE_small')};
`;

function getViewProps(
  view: DatabaseView,
  isUnsavedView?: boolean,
  isDefault = false
): { name: string; icon: ReactChild } {
  const type = view.settings.layout.type;
  return {
    icon: isUnsavedView ? (
      <LinkBrokenIcon label="" spacing="spacious" />
    ) : (
      getLayoutIcons({
        type,
        iconSize: 'medium',
        isDefault,
        isPresentationOnly: false,
      })?.icon
    ),
    name: view.name,
  };
}

export interface ViewCardProps {
  view: DatabaseView;
  isActive?: boolean;
  isDefault: boolean;
  onClick: (view: DatabaseView) => void;
  subtleText?: string;
  isReadonly?: boolean;
  contentId: string;
  existingViewNames?: string[];
  setDuplicateViewNameErrorId: (value: string) => void;
  hasDuplicateNameError: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  handleClose: () => void;
  showViewSettings?: boolean;
}

const ViewCard: FunctionComponent<ViewCardProps> = ({
  view,
  isActive,
  isDefault,
  onClick,
  subtleText,
  isReadonly,
  contentId,
  existingViewNames,
  setDuplicateViewNameErrorId,
  hasDuplicateNameError,
  openViewSettingsPanel,
  handleClose,
  showViewSettings = true,
}) => {
  const [isViewActionsOpen, setIsViewActionsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const client = useAnalytics();
  const { formatMessage } = useIntl();

  const readonlyHoverPadding = isMobile() ? token('space.400', '32px') : '70px';
  const maxWidthOpenDefault = isMobile() ? '120px' : '190px';
  const maxWidthOpenNonDefault = isMobile() ? '160px' : '240px';

  const showViewSettingAction = isPluginEnabled(PluginKey.VIEW_SETTINGS_ACTIONS);
  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      await onClick(view);
    },
    [view, onClick]
  );

  const onCopyViewLink = useCopyViewLink(contentId);

  const handleCopyViewLink = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const result = await onCopyViewLink(view);

      if (result) {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 3000);
        await client.sendViewLinkCopiedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          source: 'databaseViewPicker',
          viewId: view.id,
        });
      } else {
        showErrorSnackbar(
          'failed-to-copy-error',
          formatMessage(viewsMessages['view-actions.failed-to-copy-error'])
        );
      }
    },
    [client, contentId, formatMessage, onCopyViewLink, view]
  );

  const { icon, name } = getViewProps(view, isUnsavedView(view), isDefault);

  const getTranslatedName = useMemo(() => {
    if (isUnsavedView(view)) {
      return formatMessage(messages['view.unsaved-view']);
    }

    return name;
  }, [formatMessage, name, view]);

  const tooltipContent = useMemo(() => {
    if (isMobile()) {
      return '';
    }
    if (isUnsavedView(view)) {
      return formatMessage(messages['toolbar-views.embed-visible-tooltip']);
    } else if (isDefault) {
      return formatMessage(messages['toolbar-views.default-view-tooltip'], {
        isEmbed: isEmbed(),
      });
    } else {
      return '';
    }
  }, [formatMessage, view, isDefault]);

  return (
    <ValidationStatus
      isTouched={hasDuplicateNameError}
      placement={Placement.BOTTOM}
      error={
        hasDuplicateNameError
          ? formatMessage(messages['toolbar-views.duplicate-view-warning-text'])
          : undefined
      }
    >
      {(validationProps) => (
        <Tooltip content={tooltipContent} position="top">
          <Container
            isActive={isActive}
            onClick={handleClick}
            isDefault={isDefault}
            isViewActionsOpen={isViewActionsOpen}
            isUnsavedView={isUnsavedView(view)}
            showViewSettings={showViewSettings}
            isReadonly={isReadonly}
            $readonlyHoverPadding={readonlyHoverPadding}
            $maxWidthOpenDefault={maxWidthOpenDefault}
            $maxWidthOpenNonDefault={maxWidthOpenNonDefault}
          >
            {icon}
            <Flex alignItems="start" direction="column" xcss={xcss({ flexGrow: 1 })}>
              <Flex
                alignItems="center"
                columnGap="space.050"
                justifyContent="space-between"
                xcss={xcss({
                  flexGrow: 1,
                  width: '100%',
                  minHeight: token('space.300', '1.5rem'),
                })}
              >
                <ViewCardNameEdit
                  name={getTranslatedName}
                  existingViewNames={existingViewNames}
                  view={view}
                  setDuplicateViewNameErrorId={setDuplicateViewNameErrorId}
                  validationProps={validationProps}
                  isActive={isActive}
                  isDefault={isDefault}
                  isViewActionsOpen={isViewActionsOpen}
                  isReadonly={isReadonly}
                />
                <Flex alignItems="center">
                  <>
                    {isDefault && (
                      <DefaultText className="view-card-default-text">
                        {formatMessage(messages['toolbar-views.default-text'])}
                      </DefaultText>
                    )}
                    {showViewSettings && (
                      <Tooltip
                        content={formatMessage(
                          isCopied
                            ? messages['copy-link.tooltip']
                            : viewsMessages['view-settings.copy-link']
                        )}
                      >
                        <HoverButton
                          iconBefore={
                            <LinkIcon
                              spacing="spacious"
                              label={formatMessage(
                                viewsMessages['view-settings.copy-link']
                              )}
                            />
                          }
                          onClick={handleCopyViewLink}
                          appearance="subtle"
                          spacing="compact"
                          $isActive={isActive}
                          $isViewActionsOpen={isViewActionsOpen}
                          className="view-card-button"
                        />
                      </Tooltip>
                    )}
                    {!isUnsavedView(view) &&
                      showViewSettings &&
                      showViewSettingAction && (
                        <ViewActions
                          contentId={contentId}
                          view={view}
                          isViewActive={isActive}
                          isOpen={isViewActionsOpen}
                          setIsViewActionsOpen={setIsViewActionsOpen}
                          isDefault={isDefault}
                          isReadonly={isReadonly}
                          openViewSettingsPanel={openViewSettingsPanel}
                          handleCloseViewPicker={handleClose}
                        />
                      )}
                  </>
                </Flex>
              </Flex>

              <SubtleText>{subtleText}</SubtleText>
            </Flex>
          </Container>
        </Tooltip>
      )}
    </ValidationStatus>
  );
};

export default ViewCard;
